import { PATH_STORES } from "@/constants";
import { FilterType } from "@/lib/filters";
import { useProjectStoresQuery } from "@/modules/projects/hooks/useProjectStoresQuery";
import { ComponentProps, forwardRef, useCallback } from "react";
import { ControllerRenderProps } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AsyncSelect, { Option } from "../../../components/AsyncSelect";
import { EntityListLink } from "../../../components/EntityListLink";
import appConfiguration from "@/components/cfg/AppCfg";
import { ProjectStoreService } from "@/modules/projects/service/projectStore";

interface ProjectStoreSelectProps extends ControllerRenderProps {
  className?: string;
  inDialog?: boolean;
  clear?: boolean;
  showEntityLink?: boolean;
  placeholder?: string;
  projectId?: string;
  disabled?: boolean;
  defaultItem?: Partial<Option>;
}

export default forwardRef<HTMLButtonElement, ProjectStoreSelectProps>(
  function ProjectStoreSelect(
    {
      value,
      inDialog,
      showEntityLink = true,
      projectId,
      disabled,
      defaultItem,
      ...props
    },
    ref,
  ) {
    const { t } = useTranslation();

    const { options } = useProjectStoresQuery({
      name: "projectStoresSelect",
      projectId: projectId ?? "",
      enabled: !!projectId,
      filters: [
        {
          id: "project_id",
          value: {
            value: projectId,
            filter: FilterType.EqualsString,
          },
        },
      ],
    });

    const onFilter: ComponentProps<typeof AsyncSelect>["onChange"] =
      useCallback(
        (filterValue) => {
          options.setFilterValue("name", {
            value: filterValue,
            filter: FilterType.IncludesString,
          });
        },
        [options],
      );

    return (
      <span className="flex items-center gap-1">
        <AsyncSelect
          queryKey={["projectStoresSelect", projectId, options.filters]}
          queryFn={() =>
            ProjectStoreService.getList(
              {
                filters: [
                  ...options.filters,
                  {
                    id: "project_id",
                    value: {
                      value: projectId,
                      filter: FilterType.EqualsString,
                    },
                  },
                ],
                sorting: options.sorting[0],
                limit: appConfiguration.selectComponentsPaginationLimit,
              },
              `/${projectId}/stores`,
            )
          }
          placeholder={t("storeSelect.placeholder")}
          loadingLabel={t("storeSelect.loadingStores")}
          noResultsLabel={t("storeSelect.noResults")}
          searchLabel={t("storeSelect.search")}
          enableFilter
          {...props}
          onFilter={onFilter}
          value={value ? value.toString() : undefined}
          ref={ref}
          inDialog={inDialog}
          disabled={disabled}
          defaultItem={defaultItem}
        />
        {showEntityLink && (
          <EntityListLink path={PATH_STORES} label={t("storeSelect.link")} />
        )}
      </span>
    );
  },
);
